/**
 * Header de la app — barra superior fija.
 *
 * Contiene:
 *   - Título y subtítulo de la vista actual (derivados de la ruta).
 *   - Badge de rol del usuario activo.
 *   - LocationSwitcher (multi-GLN con reauth).
 *   - Avatar con dropdown (perfil + logout).
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, RefreshCw, Loader2, Search, FileJson } from 'lucide-react';
import { toast } from '@/lib/notify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '@/stores/session';
import { queryKeys, useIngestaManualHabilitada, useLastErpTaskSync } from '@/api/queries';
import { erpTaskApi } from '@/api/endpoints';
import { useAuditStore } from '@/stores/audit';
import { performLogout } from '@/features/auth/logout-flow';
import { LocationSwitcher } from './LocationSwitcher';
import { IngestaJsonDialog } from './IngestaJsonDialog';
// NotificacionesBell oculto — ver comentario abajo donde se renderiza.
import { EstadoAnmatIndicator } from './EstadoAnmatIndicator';
import { useState } from 'react';

interface PageInfo {
  title: string;
  subtitle: string;
}

/**
 * Avatar del usuario — `<img>` si hay foto de Google, sino texto con
 * iniciales. Si la foto falla a cargar (CSP, link expirado, sin red), cae
 * automáticamente a las iniciales. El `referrerPolicy="no-referrer"` evita
 * que Google rechace la request por referrer del front.
 */
function UserAvatar({
  avatarUrl,
  iniciales,
  nombre,
}: {
  avatarUrl: string | null;
  iniciales: string;
  nombre: string;
}) {
  const [failed, setFailed] = useState(false);
  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt={nombre}
        referrerPolicy="no-referrer"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  return <>{iniciales}</>;
}

const PAGE_TITLES: Record<string, PageInfo> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Resumen general del sistema' },
  '/despacho': { title: 'Despacho', subtitle: 'Remitos pendientes de trazar al SNT' },
  '/recepcion': { title: 'Recepción', subtitle: 'Transferencias entrantes pendientes de confirmar' },
  '/audit': { title: 'Audit Trail', subtitle: 'Registro inmutable de acciones (GAMP 5)' },
  '/apilog': { title: 'Logs API', subtitle: 'Interacciones con ERP y SNT' },
  '/series': { title: 'Series', subtitle: 'Inventario completo de series con filtros' },
  '/ingreso': { title: 'Ingreso / Alta', subtitle: 'Alta manual de series (no se informa al SNT)' },
  '/lotes': { title: 'Lotes', subtitle: 'Visor consolidado de altas manuales' },
  '/anmat': { title: 'Log ANMAT', subtitle: 'Estado de transacciones en el SNT · reintentar / editar / cancelar' },
  '/consulta-gln': { title: 'Consulta GLN ANMAT', subtitle: 'Consulta en vivo de un GLN contra el SNT · solo lectura' },
  '/trazabilidad': { title: 'Trazabilidad', subtitle: 'Timeline completo de una serie individual a través de todas las ubicaciones' },
  '/reportes': { title: 'Reportes', subtitle: 'Movimientos · Cruce ERP↔SNT · Destrucciones' },
  '/reportes/movimientos': { title: 'Reportes · Movimientos', subtitle: 'Libro de movimientos informados al SNT' },
  '/reportes/cruce': { title: 'Reportes · Cruce ERP ↔ SNT', subtitle: 'Remitos del ERP vs series trazadas en el SNT' },
  '/reportes/destrucciones': { title: 'Reportes · Destrucciones', subtitle: 'Operaciones de destrucción registradas' },
  '/maestros': { title: 'Maestros', subtitle: 'Productos · Clientes · Eventos ANMAT' },
  '/maestros/productos': { title: 'Maestros · Productos', subtitle: 'Catálogo de GTINs' },
  '/maestros/clientes': { title: 'Maestros · Clientes', subtitle: 'Clientes externos y domicilios de entrega' },
  '/maestros/eventos': { title: 'Maestros · Eventos ANMAT', subtitle: 'Catálogo SNT de tuplas regulatorias' },
  '/seguridad': { title: 'Seguridad', subtitle: 'Usuarios · Roles · Sesiones · Política' },
  '/seguridad/usuarios': { title: 'Seguridad · Usuarios', subtitle: 'Listado y estadísticas del audit trail' },
  '/seguridad/roles': { title: 'Seguridad · Roles y permisos', subtitle: 'Matriz de acciones permitidas por rol' },
  '/seguridad/sesiones': { title: 'Seguridad · Sesiones activas', subtitle: 'Sesión actual + historial de eventos' },
  '/seguridad/politica': { title: 'Seguridad · Política de sesión', subtitle: 'Timeout por inactividad configurable (GAMP 5 §11.10(d))' },
  '/manual': { title: 'Manual de uso', subtitle: 'Guía paso a paso del sistema' },
  '/handoff': { title: 'Handoff técnico', subtitle: 'Spec para el equipo de desarrollo' },
};

/**
 * Color de fondo del header POR ENTORNO — para no confundir QA con PROD.
 * Se setea con la env var de build `VITE_ENV_COLOR` (ej. QA = "#ff9424").
 * Si no está seteada, el header queda blanco (default, ej. PROD).
 * OJO: es build-time (Vite la hornea en el bundle), así que se define en el
 * build de cada entorno.
 */
const ENV_COLOR = (import.meta.env.VITE_ENV_COLOR as string | undefined)?.trim() || null;

function getPageInfo(pathname: string): PageInfo {
  // Exact match primero
  const exact = PAGE_TITLES[pathname];
  if (exact) return exact;
  // Path dinámicos
  if (pathname.startsWith('/operaciones/')) {
    return {
      title: 'Detalle de operación',
      subtitle: 'Datos del remito · series · mensajes ANMAT · log API · observaciones',
    };
  }
  return { title: 'Riga Traza', subtitle: '' };
}

export function Header() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const usuario = useSessionStore((s) => s.usuario);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);

  const { title, subtitle } = getPageInfo(location.pathname);
  const rolBadgeClass =
    usuario?.rol === 'Administrador'
      ? 'bg-purple-100 text-purple-800'
      : usuario?.rol === 'Responsable de Logística'
        ? 'bg-orange-100 text-orange-800'
        : 'bg-blue-100 text-blue-800';
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const [isSyncing, setIsSyncing] = useState(false);
  // El "último sync" se persiste en `audit_log` y lo traemos del backend —
  // así sobrevive entre sesiones y refleja el dato real, no el de memoria.
  const { data: lastSync } = useLastErpTaskSync(ubicacionActivaId);
  const ultimoSyncLabel = formatLastSync(lastSync?.at ?? null);

  // Ingesta manual de JSON (pruebas del pickeo): solo Administrador y solo si
  // el back la tiene habilitada (ERP_INGESTA_MANUAL). El back re-valida ambas.
  const esAdmin = usuario?.rol === 'Administrador';
  const { data: ingestaManual } = useIngestaManualHabilitada(esAdmin);
  const [ingestaOpen, setIngestaOpen] = useState(false);

  const handleLogout = () => performLogout({ navigate, queryClient });

  // Sincronizar = dispara el sync del ERP (POST /api/erp-task/sync) para el
  // GLN activo y refresca anmat-status + ubicaciones. Saca la consulta
  // directa de pendientes (que pegaba a ACSIS desde el front) — ahora la
  // fuente de verdad de las tareas es la tabla local `erp_task`.
  const handleSincronizar = async () => {
    if (!ubicacionActivaId) {
      toast.error('No hay GLN activo. Elegí un GLN antes de sincronizar.');
      return;
    }
    setIsSyncing(true);
    try {
      const result = await erpTaskApi.syncErpTasks(ubicacionActivaId);
      await Promise.all([
        // KPI "Remitos por trazar" + listado de erp_task (cualquier glnId/estado).
        queryClient.refetchQueries({ queryKey: ['erp-tasks'] }),
        // KPIs "Errores ANMAT" y "Series trazadas hoy" — ambos vienen del
        // mismo endpoint /api/traza/stats (prefix `traza-stats` agrupa todos
        // los glnId cacheados).
        queryClient.refetchQueries({ queryKey: ['traza-stats'] }),
        queryClient.refetchQueries({ queryKey: queryKeys.anmatStatus }),
        queryClient.refetchQueries({ queryKey: queryKeys.ubicaciones }),
        // Refresca el "última sincronización" leído de audit_log.
        queryClient.refetchQueries({ queryKey: ['erp-task-last-sync'] }),
      ]);
      const hh = new Date().toTimeString().slice(0, 5);
      // Remitos anulados en Plataforma: sin trazar (salen de pendientes) y de
      // trazas canceladas (pendientes de baja).
      const anulados =
        (result.anulaciones?.tareas.anuladas ?? 0) + (result.anulaciones?.remitos.anulados ?? 0);
      toast.success(
        `Sync OK · ${result.recibidas} tareas del ERP` +
          (anulados > 0 ? ` · ${anulados} remito(s) anulado(s) en Plataforma` : '') +
          ` · ${hh}`,
      );
      if (anulados > 0) {
        void queryClient.invalidateQueries({ queryKey: ['tareas-pendientes'] });
        void queryClient.invalidateQueries({ queryKey: queryKeys.despachoCompletadas });
      }
      addAuditEntry({
        categoria: 'operacion_critica',
        accion: 'sync.manual',
        razon: `Sync ERP (GLN ${result.glnCode}) · ${result.recibidas} recibidas, ${result.totalEnDb} totales`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al sincronizar';
      toast.error(`Error al sincronizar: ${msg}`);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 px-8 py-4',
        !ENV_COLOR && 'bg-white',
      )}
      style={ENV_COLOR ? { backgroundColor: ENV_COLOR } : undefined}
    >
      <div>
        <h1 className="text-xl font-bold text-everex-azul">{title}</h1>
        {subtitle && <p className="text-xs text-gray-600">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-4">
        {/* Command Palette trigger — discreto, descubre el atajo Cmd+K (paquete AD).
            Texto "Acciones rápidas" para no chocar con el botón "Buscar" de
            TrazabilidadPage en selectores E2E. */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
          className="hidden h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 text-xs text-gray-600 transition hover:border-everex-naranja hover:bg-white hover:text-everex-azul md:flex"
          title="Acciones rápidas / navegar (Cmd+K)"
          aria-label="Abrir paleta de comandos"
        >
          <Search className="h-3 w-3" />
          <span>Acciones rápidas</span>
          <kbd className="ml-2 rounded border border-gray-200 bg-white px-1 py-0 font-mono text-[11px]">
            ⌘K
          </kbd>
        </button>

        {/* Sincronizar ERP+SNT — dispara polling de tareas + recepciones pendientes */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleSincronizar()}
          disabled={isSyncing}
          className="flex items-center gap-2 text-xs"
          title="Sincronizar registros con el ERP"
        >
          {isSyncing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          <span className="font-semibold">Sincronizar</span>
          <span className="text-[11px] text-gray-600">{ultimoSyncLabel}</span>
        </Button>

        {esAdmin && ingestaManual?.habilitada && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIngestaOpen(true)}
            className="flex items-center gap-2 text-xs"
            title="Crear una tarea de prueba pegando el JSON de un comprobante (no consulta al ERP)"
          >
            <FileJson className="h-3 w-3" />
            <span className="font-semibold">Ingesta de JSON</span>
          </Button>
        )}
        <IngestaJsonDialog
          open={ingestaOpen}
          onOpenChange={setIngestaOpen}
          glnId={ubicacionActivaId}
        />

        {/* Location switcher (multi-GLN) */}
        <LocationSwitcher />

        {/* Notificaciones bell — oculto temporalmente hasta que tengamos el
            stream server-side (SSE/WebSocket). Por ahora todo era client-local.
            El store (useNotificacionesStore) sigue funcionando para que las
            mutations puedan llamar a push() sin romperse. */}

        {/* Estado ANMAT — health check en vivo (proxy a riga-traza-v2, polling 10s). */}
        <EstadoAnmatIndicator />

        {/* Avatar con dropdown — foto real si login fue con Google, sino
            iniciales en círculo naranja. */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex h-10 w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-everex-naranja font-bold text-white transition hover:bg-everex-naranja-hover"
            title="Cuenta"
            aria-label="Menú de usuario"
          >
            <UserAvatar
              avatarUrl={usuario?.avatarUrl ?? null}
              iniciales={usuario?.iniciales ?? '—'}
              nombre={usuario?.nombre ?? ''}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[220px]">
            <DropdownMenuLabel>
              <div className="font-semibold text-everex-azul">{usuario?.nombre ?? '—'}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs font-normal">
                <span
                  className={cn(
                    'rounded px-2 py-0.5 text-[11px] font-semibold',
                    rolBadgeClass,
                  )}
                >
                  {usuario?.rol ?? '—'}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => void handleLogout()}
              className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span className="font-semibold">Cerrar sesión</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

/**
 * Formatea el ISO del último sync para mostrarlo al lado del botón.
 * - Sin sync registrado → "—".
 * - Mismo día → "HH:MM".
 * - Otro día → "dd/mm HH:MM".
 */
function formatLastSync(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mo} ${hh}:${mm}`;
}
